import { FC, useState, useRef, useEffect } from "react";
import { Settings, LogOut, ChevronUp } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";

interface UserMenuProps {
  collapsed: boolean;
}

export const UserMenu: FC<UserMenuProps> = ({ collapsed }) => {
  const { user, handleLogout, userDisplayName } = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState({ bottom: 0, left: 0, width: 0 });

  const initials = userDisplayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({
        bottom: window.innerHeight - rect.top + 4,
        left: rect.left,
        width: rect.width,
      });
    }
    setOpen(!open);
  };

  return (
    <div ref={menuRef} className="px-2">
      {/* Dropdown menu - fixed position to escape overflow clipping */}
      {open && (
        <div
          className="fixed bg-white rounded-lg shadow-card-hover border border-border-light overflow-hidden z-50"
          style={{
            bottom: menuPos.bottom,
            left: menuPos.left,
            width: menuPos.width,
          }}
        >
          {!collapsed && (
            <div className="px-3 py-2.5 border-b border-border-light">
              <p className="text-xs text-text-muted truncate">{user?.email}</p>
            </div>
          )}
          <button
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm text-text-body hover:bg-bg-light transition-colors text-left"
          >
            <Settings size={16} strokeWidth={1.8} />
            {!collapsed && <span>Settings</span>}
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm text-danger hover:bg-red-50 transition-colors text-left"
          >
            <LogOut size={16} strokeWidth={1.8} />
            {!collapsed && <span>Log out</span>}
          </button>
        </div>
      )}

      {/* Trigger button */}
      <button
        ref={triggerRef}
        onClick={handleToggle}
        title={collapsed ? userDisplayName : undefined}
        className={`flex items-center w-full rounded-lg transition-all duration-200 hover:bg-bg-light
          ${collapsed ? "justify-center p-2" : "gap-3 px-3 py-2.5"}`}
      >
        <div className="shrink-0 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-xs font-semibold">
          {initials}
        </div>
        {!collapsed && (
          <>
            <div className="flex-1 text-left min-w-0">
              <p className="text-sm font-medium text-text-heading truncate">
                {userDisplayName}
              </p>
            </div>
            <ChevronUp
              size={16}
              className={`shrink-0 text-text-muted transition-transform duration-200 ${
                open ? "" : "rotate-180"
              }`}
            />
          </>
        )}
      </button>
    </div>
  );
};
